import {
  AfterViewInit, ApplicationRef, ChangeDetectionStrategy, ChangeDetectorRef,
  Component, ElementRef, EmbeddedViewRef, OnDestroy, TemplateRef, ViewChild,
} from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { MatCalendar } from '@angular/material/datepicker';
import { merge, Subscription } from 'rxjs';

@Component({
  selector: 'app-task-cloud-calendar-header',
  template: `
    <mat-calendar-header>
      <span *ngIf="hasCloudIcons" class="cloud-legend">
        <mat-icon class="task-cloud-icon" [matTooltip]="cloudTooltip" aria-hidden="true">cloud</mat-icon>
        <span>Cloud cover &lt; 20%</span>
      </span>
    </mat-calendar-header>
    <ng-template #cellCloudIcon>
      <mat-icon class="task-cloud-cell-icon" [matTooltip]="cloudTooltip"
        [attr.aria-label]="cloudTooltip" [attr.aria-hidden]="false">cloud</mat-icon>
    </ng-template>
  `,
  styles: [`
    .cloud-legend {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      white-space: nowrap;
      font-size: 9px;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskCloudCalendarHeaderComponent implements AfterViewInit, OnDestroy {
  readonly cloudTooltip = 'Cloud cover below 20% at every hour from 6 AM through 6 PM (target local time) for all targets.';
  @ViewChild('cellCloudIcon', { static: true }) private cellCloudIcon!: TemplateRef<unknown>;
  hasCloudIcons = false;
  private readonly changes: Subscription;
  private cellObserver?: MutationObserver;
  private readonly cellIcons = new Map<Element, EmbeddedViewRef<unknown>>();

  constructor(
    private readonly calendar: MatCalendar<Date>,
    private readonly adapter: DateAdapter<Date>,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly element: ElementRef<HTMLElement>,
    private readonly application: ApplicationRef,
  ) {
    this.changes = merge(calendar.stateChanges, calendar.viewChanged).subscribe(() => this.refreshLegend());
    this.refreshLegend();
  }

  ngAfterViewInit(): void {
    const calendarElement = this.element.nativeElement.closest('mat-calendar');
    if (!calendarElement) return;
    // Material 14 exposes dateClass, but no date-cell template. Add only the
    // tooltip icons to those marked cells, keeping Material's buttons intact.
    this.cellObserver = new MutationObserver(() => this.syncCellIcons(calendarElement));
    this.cellObserver.observe(calendarElement, {
      childList: true, subtree: true, attributes: true, attributeFilter: ['class'],
    });
    this.syncCellIcons(calendarElement);
  }

  private syncCellIcons(calendarElement: Element): void {
    const cells = new Set(Array.from(calendarElement.querySelectorAll(
      '.task-cloud-eligible .mat-calendar-body-cell-content',
    )));
    this.cellIcons.forEach((view, cell) => {
      if (!cells.has(cell)) {
        this.application.detachView(view);
        view.destroy();
        this.cellIcons.delete(cell);
      }
    });
    cells.forEach((cell) => {
      if (this.cellIcons.has(cell)) return;
      const view = this.cellCloudIcon.createEmbeddedView({});
      this.cellIcons.set(cell, view);
      this.application.attachView(view);
      view.rootNodes.forEach((node) => cell.appendChild(node));
      view.detectChanges();
    });
  }

  private refreshLegend(): void {
    this.hasCloudIcons = false;
    if (this.calendar.currentView === 'month' && this.calendar.dateClass) {
      const activeDate = this.calendar.activeDate;
      for (let day = 1; day <= this.adapter.getNumDaysInMonth(activeDate); day++) {
        const date = this.adapter.createDate(
          this.adapter.getYear(activeDate), this.adapter.getMonth(activeDate), day,
        );
        if (this.calendar.dateClass(date, 'month') === 'task-cloud-eligible') {
          this.hasCloudIcons = true;
          break;
        }
      }
    }
    this.changeDetector.markForCheck();
  }

  ngOnDestroy(): void {
    this.cellObserver?.disconnect();
    this.cellIcons.forEach((view) => {
      this.application.detachView(view);
      view.destroy();
    });
    this.cellIcons.clear();
    this.changes.unsubscribe();
  }
}
